import { Button, Card, Divider, Form, Input, notification, Radio, Skeleton, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getKubeService } from "../../backend";
import { EyeOff, Eye } from "lucide-react";
import { RequiredMark } from "antd/es/form/Form";
import { ECR_PULL_OPTIONS, ecrPullSecretOf, type ClusterUpdateRequest, type EcrPullSecret } from "../../v2/data/clusters";

export default function KubernetesSettingsGeneral() {
    const { projectId, envId, clusterId } = useParams() as {
        projectId: string;
        envId: string;
        clusterId: string;
    };
    const [requiredMark] = useState<RequiredMark>('optional');
    const [loading, setLoading] = useState(true);
    const [editingCluster, setEditingCluster] = useState(false);
    const [savedEcrPull, setSavedEcrPull] = useState<EcrPullSecret>("auto");
    const [form] = Form.useForm();

    useEffect(() => {
        const load = async () => {
            const { data: cluster } = await getKubeService().getKubernetes(clusterId, projectId);
            const ecrPullSecret = ecrPullSecretOf(cluster as { ecrPullSecret?: string });
            form.setFieldsValue({
                name: cluster.name,
                endpoint: cluster.endpoint,
                apiToken: "(sensitive)",
                ecrPullSecret
            });
            setSavedEcrPull(ecrPullSecret);
            setLoading(false);
        }

        load();
    }, []);

    const handleUpdate = async () => {
        try {
            const { name, apiToken, endpoint, ecrPullSecret } = await form.validateFields();

            setEditingCluster(true);

            // The pull setting is sent only when it changed
            const request: ClusterUpdateRequest = {
                name,
                endpoint,
                ...(apiToken !== "(sensitive)" ? { apiToken } : {}),
                ...(ecrPullSecret !== savedEcrPull ? { ecrPullSecret } : {})
            };
            await getKubeService().updateKubernetes(clusterId, projectId, request);
            setSavedEcrPull(ecrPullSecret);
            notification.success({
                message: `Cluster updated`,
                description: `Cluster ${name} was successfully updated`,
            });

        } catch (error) {
            if ((error as any).errorFields) {
                notification.error({
                    message: "Error updating cluster",
                    description: "Cluster configuration is not valid."
                });
            } else {
                notification.error({
                    message: "Error updating cluster",
                    description: (error as any).response?.data?.message
                });
            }
        } finally {
            setEditingCluster(false);
        }
    }

    return (
        <Space direction="vertical" className="full-width">
            <Card bordered>
                <Card.Meta
                    title="Cluster Configuration"
                    description="Configure access to your Kubernetes cluster"
                />
                <Divider />
                {
                    loading ? <Skeleton /> :
                        <>
                            <Form
                                form={form}
                                layout="vertical"
                                initialValues={{}}
                                requiredMark={requiredMark}
                            >
                                <Form.Item
                                    name="name"
                                    label="Name"
                                    required
                                    rules={[{ required: true, message: 'Please input the Kubernetes Name!' }]}
                                >
                                    <Input autoFocus={!!envId} />
                                </Form.Item>
                                <Form.Item
                                    name="endpoint"
                                    label="Endpoint"
                                    required
                                    rules={[
                                        { required: true, message: 'Please input the Kubernetes Endpoint!' }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                                <Form.Item
                                    name="apiToken"
                                    label="API token"
                                    required
                                    rules={[{ required: true, message: 'Please input the API token!' }]}
                                >
                                    <Input.Password iconRender={visible => (visible ? <Eye size={14} /> : <EyeOff size={14} />)} />
                                </Form.Item>
                                <Form.Item
                                    name="ecrPullSecret"
                                    label="Image pull credentials (ECR)"
                                    extra="Other registries always use a pull secret."
                                >
                                    <Radio.Group>
                                        <Space direction="vertical">
                                            {ECR_PULL_OPTIONS.map(o => (
                                                <Radio key={o.value} value={o.value}>
                                                    {o.label}
                                                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>{o.hint}</Typography.Text>
                                                </Radio>
                                            ))}
                                        </Space>
                                    </Radio.Group>
                                </Form.Item>
                                <div className="text-right">
                                    <Button
                                        onClick={handleUpdate}
                                        loading={editingCluster}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </Form>
                        </>
                }
            </Card>
        </Space>
    )
}